<template>
  <v-bottom-sheet v-model="dialogVisible" max-width="1920" class="energy-storage-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题栏 -->
      <div class="dialog-title">
        <div class="title-left">
          <v-btn icon variant="text" class="back-btn" @click="closeDialog">
            <v-icon>mdi-reply-outline</v-icon>
            返回
          </v-btn>
          <span class="title-text">{{ eblock?.name }} - 详情</span>
        </div>
        <div class="time-info">
          <span class="grid-time">投运时间：{{ gridTime }}</span>
          <span class="sample-time">采样时间：{{ currentTime }}</span>
        </div>
        <div class="title-right">
          <!-- <v-btn icon variant="text" @click="fetchEblockDetail">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn> -->
          <v-btn icon variant="text" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- 主内容区域 -->
      <div class="dialog-main detail-dialog-main">
        <!-- 左侧：图片 -->
        <div class="detail-left-content">
          <div class="enlarged-image-container">
            <img :src="getBlockImage(eblock.model)" class="enlarged-device-img" />
            <div class="enlarged-device-name">{{ eblock.model || '--' }}</div>
          </div>
        </div>

        <!-- 右侧：详细内容 (三列布局) -->
        <div class="detail-right-content">
          <div class="section-title">遥测值</div>

          <div class="three-column-layout">
            <!-- 第一列 -->
            <div class="column-group">
              <!-- 顶部大卡片：电池剩余电量 -->
              <div class="info-card large-card">
                <div class="battery-visual">
                  <div class="battery-icon">
                    <div class="battery-level" :style="{ width: (detailData.soc || 0) + '%' }">
                    </div>
                  </div>
                </div>
                <div class="card-content">
                  <div class="card-label">电池剩余电量</div>
                  <div class="card-value-large">{{ formatValue(detailData.e_remain) }}<span class="unit"
                      v-if="detailData.e_remain != null">KWh</span></div>
                </div>
              </div>
              <!-- 底部小卡片网格 -->
              <div class="small-cards-grid">
                <div class="grid-card" v-for="(item, index) in column1Items" :key="index">
                  <div class="card-top">
                    <div class="card-value">{{ item.value }}<span class="unit" v-if="item.unit">{{
                      item.unit }}</span></div>
                    <div class="card-label">{{ item.label }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 第二列 -->
            <div class="column-group">
              <!-- 顶部大卡片：电站SOC -->
              <div class="info-card large-card center-content">
                <div class="progress-wrapper">
                  <div class="progress-header">
                    <span class="label">电站SOC</span>
                    <span class="value">{{ formatValue(detailData.soc) }}<span
                        v-if="detailData.soc != null">%</span></span>
                  </div>
                  <div class="soc-progress-bar">
                    <div class="soc-fill" :style="{ width: (detailData.soc || 0) + '%' }"></div>
                  </div>
                </div>
              </div>
              <!-- 底部小卡片网格 -->
              <div class="small-cards-grid">
                <div class="grid-card" v-for="(item, index) in column2Items" :key="index">
                  <div class="card-top">
                    <div class="card-value">{{ item.value }}<span class="unit" v-if="item.unit">{{
                      item.unit }}</span></div>
                    <div class="card-label">{{ item.label }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 第三列 -->
            <div class="column-group">
              <!-- 顶部大卡片：电站SOH -->
              <div class="info-card large-card center-content">
                <div class="progress-wrapper">
                  <div class="progress-header">
                    <span class="label">电站SOH</span>
                    <span class="soh-label-value">{{ sohLabel }}</span>
                  </div>
                  <div class="soh-progress-bar">
                    <div class="soh-fill" :style="{ width: (detailData.soh || 0) + '%' }"></div>
                  </div>
                </div>
              </div>
              <!-- 底部小卡片网格 -->
              <div class="small-cards-grid">
                <div class="grid-card" v-for="(item, index) in column3Items" :key="index">
                  <div class="card-top">
                    <div class="card-value">{{ item.value }}<span class="unit" v-if="item.unit">{{
                      item.unit }}</span></div>
                    <div class="card-label">{{ item.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import dayjs from 'dayjs'
import { ws_req } from '@/ws/wss-request'
import { useStationContext } from '../../composables/useStationContext'
import { column1Fields, column2Fields, column3Fields, updateColumnItems } from './monitorEnums'

// 导入所有 block-main 图片
import blockMain172 from '@/assets/images/station-monitor/detail-dialog/block-main-172.png'
import blockMain200 from '@/assets/images/station-monitor/detail-dialog/block-main-200.png'
import blockMain230 from '@/assets/images/station-monitor/detail-dialog/block-main-230.png'
import blockMain250 from '@/assets/images/station-monitor/detail-dialog/block-main-250.png'
import blockMain261 from '@/assets/images/station-monitor/detail-dialog/block-main-261.png'
import blockMain372 from '@/assets/images/station-monitor/detail-dialog/block-main-372.png'
import blockMain836 from '@/assets/images/station-monitor/detail-dialog/block-main-836.png'

// block 图片映射表
const blockImageMap = {
  '172': blockMain172,
  '200': blockMain200,
  '230': blockMain230,
  '250': blockMain250,
  '261': blockMain261,
  '372': blockMain372,
  '836': blockMain836
}

// 支持的 model 数字列表
const supportedModels = ['172', '200', '230', '250', '261', '372', '836']

// 根据 model 获取对应的 block 图片
const getBlockImage = (model) => {
  if (!model) return blockMain200
  // 从 model 字符串中提取数字
  const match = String(model).match(/\d+/)
  if (match) {
    const modelNumber = match[0]
    if (supportedModels.includes(modelNumber)) {
      return blockImageMap[modelNumber]
    }
  }
  // 默认返回 200
  return blockMain200
}

// 格式化数值，区分 0 和 无数据
const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '--'
  return val
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  eblock: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId } = useStationContext()

const currentTime = ref('--')
const gridTime = ref('--')
const pollingTimer = ref(null)
const detailData = ref({})

// SOH 评估函数
const appraiseFromSOH = (soh) => {
  if (soh >= 95) {
    return '极佳'
  }
  if (soh >= 90) {
    return '佳'
  }
  if (soh >= 80) {
    return '优秀'
  }
  if (soh >= 70) {
    return '良好'
  }
  if (soh >= 65) {
    return '合格'
  }
  if (soh >= 60) {
    return '建议更换'
  }
  return '停止运行'
}

// 计算 SOH 标签
const sohLabel = computed(() => {
  const soh = detailData.value.soh
  if (soh === undefined || soh === null || soh === '') {
    return '--'
  }
  return appraiseFromSOH(Number(soh))
})

const closeDialog = () => {
  dialogVisible.value = false
}

// 第一列数据
const column1Items = ref(column1Fields.map(field => ({
  key: field.key,
  label: field.label,
  value: '--',
  unit: field.unit
})))

// 第二列数据
const column2Items = ref(column2Fields.map(field => ({
  key: field.key,
  label: field.label,
  value: '--',
  unit: field.unit
})))

// 第三列数据
const column3Items = ref(column3Fields.map(field => ({
  key: field.key,
  label: field.label,
  value: '--',
  unit: field.unit
})))

// 更新详情数据
const updateDetailData = (data) => {
  if (!data) return

  // 更新采样时间
  if (data.record_time) {
    currentTime.value = dayjs(data.record_time).format('YYYY-MM-DD HH:mm:ss')
  }
  gridTime.value = data?.on_grid_time?.split('.')[0] ?? "--";

  detailData.value = data

  // 使用 enum 中的方法更新各列数据
  column1Items.value = updateColumnItems(column1Fields, data)
  column2Items.value = updateColumnItems(column2Fields, data)
  column3Items.value = updateColumnItems(column3Fields, data)
}

// 获取 eblock 详情数据
const fetchEblockDetail = async () => {
  try {
    if (!props.eblock?.id || !currentStationId.value) {
      return
    }

    const result = await ws_req.request('ws_eblock_detail', {
      eBlockId: props.eblock.id,
      stationId: Number(currentStationId.value)
    })

    if (result && result.data) {
      updateDetailData(result.data)
    }
  } catch (error) {
    console.error('获取 eblock 详情失败:', error)
  }
}

// 开始轮询
const startPolling = () => {
  stopPolling()
  fetchEblockDetail()
  pollingTimer.value = setInterval(fetchEblockDetail, 5000) // 每5秒更新一次
}

// 停止轮询
const stopPolling = () => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
    pollingTimer.value = null
  }
}

// Watch for dialog opening to fetch data
watch(dialogVisible, (newVal) => {
  if (newVal) {
    startPolling()
  } else {
    stopPolling()
  }
})

// 清理定时器
onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped lang="scss">
.energy-storage-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 950px;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  margin-bottom: 20px;
  padding-left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
}

.title-left {
  display: flex;
  align-items: center;
}

.title-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-btn {
  width: 98px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #C0CFDA;
  margin-right: 10px;
}

.time-info {
  position: absolute;
  right: 120px;
  top: 30px;
  display: flex;
  align-items: center;
  gap: 40px;

  span {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
  }
}

.dialog-main {
  display: flex;
  gap: 30px;
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 40px !important;
  gap: 40px !important;
  overflow: hidden;
}

.detail-left-content {
  width: 448px;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.enlarged-image-container {
  width: 448px;
  height: 674px;
  background: linear-gradient(180deg, rgba(239, 244, 251, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.enlarged-device-img {
  width: 250px;
  height: auto;
  max-height: 500px;
}

.enlarged-device-name {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
}

.detail-right-content {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #2E2E2E;
  margin-bottom: 20px;
}

/* Three Column Layout */
.three-column-layout {
  display: flex;
  gap: 20px;
  flex: 1;
}

.column-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  background: #FFFFFF;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.02);
}

.large-card {
  height: 140px;
}

.center-content {
  align-items: center;
}

/* Battery Card Styles */
.battery-visual {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
}

.battery-icon {
  width: 80px;
  height: 40px;
  border: 2px solid #8DD3F5;
  border-radius: 4px;
  position: relative;
  padding: 2px;
}

.battery-icon::after {
  content: '';
  position: absolute;
  right: -5px;
  top: 8px;
  width: 3px;
  height: 10px;
  background: #8DD3F5;
  border-radius: 0 2px 2px 0;
}

.battery-level {
  height: 100%;
  background: linear-gradient(90deg, #DEF8B3 0%, #8DD3F5 100%);
  border-radius: 2px;
}

.card-content {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.card-label {
  font-size: 14px;
  color: rgba(46, 46, 46, 0.6);
}

.card-value-large {
  margin-left: 12px;
  font-size: 24px;
  font-weight: 600;
  color: #2E2E2E;

  .unit {
    font-size: 14px;
    font-weight: normal;
    margin-left: 4px;
  }
}

/* SOC/SOH Card Styles */
.progress-wrapper {
  width: 100%;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;

  .label {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.6);
  }

  .value {
    font-size: 16px;
    font-weight: 600;
    color: #2E2E2E;
  }

  .soh-label-value {
    font-size: 16px;
    font-weight: 600;
    color: #2E2E2E;
  }
}

/* SOC Progress Bar - 渐变色进度条 */
.soc-progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(233, 240, 246, 1);
  border-radius: 20px;
  overflow: hidden;

  .soc-fill {
    height: 100%;
    background: linear-gradient(270deg, rgba(141, 211, 245, 1) 5.77%, rgba(184, 229, 214, 1) 52.89%, rgba(222, 248, 179, 1) 100%);
    border-radius: 20px;
    transition: width 0.3s ease;
  }
}

/* SOH Progress Bar - 方块渐变进度条 */
.soh-progress-bar {
  width: 100%;
  height: 18px;
  background: #E9F0F6;
  /* 未填充部分的底色 */
  border-radius: 2px;
  overflow: hidden;
  /* 使用 mask 切割出方块和间隙：黑色为显示，透明为隐藏 */
  -webkit-mask: repeating-linear-gradient(90deg, #000 0, #000 6px, transparent 6px, transparent 8px);
  mask: repeating-linear-gradient(90deg, #000 0, #000 6px, transparent 6px, transparent 8px);

  .soh-fill {
    height: 100%;
    /* 使用和 SOC 一样的整体渐变色 */
    background: linear-gradient(270deg, rgba(141, 211, 245, 1) 5.77%, rgba(184, 229, 214, 1) 52.89%, rgba(222, 248, 179, 1) 100%);
    transition: width 0.3s ease;
  }
}

/* Small Cards Grid */
.small-cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.grid-card {
  background: #FFFFFF;
  border-radius: 10px;
  padding: 16px;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.02);
}

.card-top {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-value {
  font-size: 16px;
  font-weight: 600;
  color: #2E2E2E;

  .unit {
    font-size: 12px;
    font-weight: normal;
    margin-left: 2px;
    color: #666;
  }
}
</style>
